import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faHome,
  faCalendar,
  faUsers,
  faPhone,
  faChartPie,
  faCog,
  faHistory,
  faLightbulb,
  faArrowRight,
  faHandsHelping,
  faFolderOpen,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { useSearch } from '../../contexts/SearchContext';
import { highlightMatch } from '../../utils/nlpCommandService';
import {
  Overlay,
  PaletteContainer,
  SearchInputWrapper,
  SearchIcon,
  SearchInput,
  ShortcutHint,
  FilterTabs,
  FilterTab,
  FilterBadge,
  ResultsContainer,
  ResultSection,
  ResultItem,
  ResultIcon,
  ResultContent,
  ResultLabel,
  ResultSubtitle,
  ResultArrow,
  EmptyState,
  Footer,
  FooterHint,
  LoadingSpinner
} from './styled';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pages', label: 'Pages' },
  { key: 'members', label: 'Members' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'volunteers', label: 'Volunteers' },
  { key: 'calls', label: 'Calls' },
];

// Get icon for result type
const getResultIcon = (type, key) => {
  switch (type) {
    case 'page':
    case 'recent':
      // Match specific page icons
      if (key?.includes('dashboard')) return faHome;
      if (key?.includes('booking') || key?.includes('event')) return faCalendar;
      if (key?.includes('member') || key?.includes('user')) return faUsers;
      if (key?.includes('call')) return faPhone;
      if (key?.includes('volunteer')) return faHandsHelping;
      if (key?.includes('analytics')) return faChartPie;
      if (key?.includes('case')) return faFolderOpen;
      if (key?.includes('intake') || key?.includes('queue')) return faClock;
      if (key?.includes('setting')) return faCog;
      return faHome;
    case 'action':
      return faLightbulb;
    case 'member':
      return faUsers;
    case 'booking':
      return faCalendar;
    case 'volunteer':
      return faHandsHelping;
    case 'call':
      return faPhone;
    case 'history':
      return faHistory;
    case 'suggestion':
      return faLightbulb;
    default:
      return faHome;
  }
};

const CommandPalette = () => {
  const {
    isOpen,
    query,
    activeFilter,
    isSearching,
    selectedIndex,
    entityResults,
    setQuery,
    setActiveFilter,
    setSelectedIndex,
    executeResult,
    closeSearch,
    getAllResults
  } = useSearch();

  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const selectedItemRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && resultsRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  // Get all results
  const results = getAllResults();
  const selectableResults = results.filter(r => r.type !== 'section');

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < selectableResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : selectableResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectableResults[selectedIndex]) {
          executeResult(selectableResults[selectedIndex]);
        }
        break;
      case 'Tab': {
        e.preventDefault();
        const currentFilterIndex = FILTERS.findIndex(f => f.key === activeFilter);
        const nextIndex = e.shiftKey 
          ? (currentFilterIndex - 1 + FILTERS.length) % FILTERS.length
          : (currentFilterIndex + 1) % FILTERS.length;
        setActiveFilter(FILTERS[nextIndex].key);
        break;
      }
      default:
        break;
    }
  }, [selectableResults, selectedIndex, activeFilter, setSelectedIndex, setActiveFilter, executeResult]);

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeSearch();
    }
  };

  // Count results per filter
  const getFilterCount = (filterKey) => {
    switch (filterKey) {
      case 'members': return entityResults.members.length;
      case 'bookings': return entityResults.bookings.length;
      case 'volunteers': return entityResults.volunteers.length;
      case 'calls': return entityResults.calls.length;
      default: return null;
    }
  };

  // Render result label with highlighting
  const renderLabel = (label, type) => {
    if (!query || type === 'section') return label;
    
    const { parts } = highlightMatch(label, query);
    return (
      <>
        {parts.map((part, i) => 
          part.highlight ? <mark key={i}>{part.text}</mark> : part.text
        )}
      </>
    );
  };

  // Track selectable index for each result
  let selectableIndex = -1;

  if (!isOpen) return null;

  return createPortal(
    <Overlay onClick={handleOverlayClick}>
      <PaletteContainer onKeyDown={handleKeyDown}>
        {/* Search Input */}
        <SearchInputWrapper>
          <SearchIcon>
            <FontAwesomeIcon icon={faSearch} />
          </SearchIcon>
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="Search pages, members, events..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <ShortcutHint>ESC</ShortcutHint>
        </SearchInputWrapper>

        {/* Filter Tabs */}
        <FilterTabs>
          {FILTERS.map(filter => {
            const count = getFilterCount(filter.key);
            return (
              <FilterTab
                key={filter.key}
                $active={activeFilter === filter.key}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
                {count > 0 && (
                  <FilterBadge $active={activeFilter === filter.key}>
                    {count}
                  </FilterBadge>
                )}
              </FilterTab>
            );
          })}
        </FilterTabs>

        {/* Results */}
        <ResultsContainer ref={resultsRef}>
          {isSearching ? (
            <LoadingSpinner>
              <div className="spinner" />
              Searching...
            </LoadingSpinner>
          ) : results.length === 0 ? (
            <EmptyState>
              <h4>No results found</h4>
              <p>Try a different search term or filter</p>
            </EmptyState>
          ) : (
            results.map((result, index) => {
              if (result.type === 'section') {
                return (
                  <ResultSection key={`section-${index}`}>
                    {result.label}
                  </ResultSection>
                );
              }

              selectableIndex++;
              const isSelected = selectableIndex === selectedIndex;

              return (
                <ResultItem
                  key={result.key || `result-${index}`}
                  ref={isSelected ? selectedItemRef : null}
                  $selected={isSelected}
                  onClick={() => executeResult(result)}
                >
                  <ResultIcon $type={result.type}>
                    <FontAwesomeIcon icon={getResultIcon(result.type, result.key)} />
                  </ResultIcon>
                  <ResultContent>
                    <ResultLabel>
                      {renderLabel(result.label, result.type)}
                    </ResultLabel>
                    {result.subtitle && (
                      <ResultSubtitle>{result.subtitle}</ResultSubtitle>
                    )}
                  </ResultContent>
                  <ResultArrow $visible={isSelected}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </ResultArrow>
                </ResultItem>
              );
            })
          )}
        </ResultsContainer>

        {/* Footer */}
        <Footer>
          <FooterHint>
            <kbd>↑</kbd><kbd>↓</kbd> Navigate
          </FooterHint>
          <FooterHint>
            <kbd>↵</kbd> Select
          </FooterHint>
          <FooterHint>
            <kbd>Tab</kbd> Switch filter
          </FooterHint>
          <FooterHint>
            <kbd>Esc</kbd> Close
          </FooterHint>
        </Footer>
      </PaletteContainer>
    </Overlay>,
    document.body
  );
};

export default CommandPalette;
